import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  QrCodeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import QrScanner from './QrScanner';

interface ScanModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Unique DOM id for the camera element */
  elementId: string;
  /** Looks the token up; the page stores the result / error itself */
  onLookup: (token: string) => void | Promise<void>;
  isLookingUp: boolean;
  error?: string;
  /** True once the page has data for the scanned code */
  hasResult: boolean;
  /** Clears the page's result so the next code can be scanned */
  onReset: () => void;
  /** Data + actions for the scanned code */
  children?: React.ReactNode;
}

/** Manual boarding codes are 5 characters (e.g. XK7M2) */
const SHORT_CODE_LENGTH = 5;

/**
 * Popup scanner: reads one code, stops the camera, then shows the result
 * (children) until the user scans the next code or closes the popup.
 */
const ScanModal: React.FC<ScanModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  elementId,
  onLookup,
  isLookingUp,
  error,
  hasResult,
  onReset,
  children,
}) => {
  const [captured, setCaptured] = useState(false);
  const capturedRef = useRef(false);
  const [manualToken, setManualToken] = useState('');

  const resetCapture = useCallback(() => {
    capturedRef.current = false;
    setCaptured(false);
  }, []);

  // Fresh state every time the popup opens
  useEffect(() => {
    if (open) {
      resetCapture();
      setManualToken('');
    }
  }, [open, resetCapture]);

  // Esc closes the popup
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const submit = useCallback(
    (raw: string) => {
      const token = raw.trim();
      if (!token || capturedRef.current) return;
      // One read only — stop the camera before looking up
      capturedRef.current = true;
      setCaptured(true);
      void onLookup(token);
    },
    [onLookup]
  );

  const scanNext = () => {
    onReset();
    setManualToken('');
    resetCapture();
  };

  if (!open) return null;

  const scanning = !captured && !hasResult;
  const failed = captured && !isLookingUp && !hasResult && !!error;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4 rtl" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} />

      <div className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <QrCodeIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate font-semibold text-gray-900">{title}</h2>
              {subtitle && scanning && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="إغلاق"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {scanning && (
            <div className="space-y-4">
              <QrScanner active={scanning} onScan={submit} elementId={elementId} />

              <div>
                <p className="mb-1.5 text-xs text-gray-500">
                  أو اكتب الرمز القصير — تظهر البيانات تلقائياً عند اكتمال الـ {SHORT_CODE_LENGTH} أحرف
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit(manualToken);
                  }}
                >
                  <input
                    type="text"
                    value={manualToken}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[\s-]/g, '').toUpperCase();
                      setManualToken(value);
                      // Short code complete → look it up without pressing anything
                      if (value.length === SHORT_CODE_LENGTH) submit(value);
                    }}
                    placeholder="XK7M2"
                    className="input-field w-full text-center font-mono text-lg uppercase tracking-[0.3em]"
                    dir="ltr"
                    autoComplete="off"
                    autoCapitalize="characters"
                    enterKeyHint="search"
                    maxLength={40}
                  />
                </form>
              </div>
            </div>
          )}

          {captured && isLookingUp && !hasResult && (
            <div className="flex flex-col items-center gap-3 py-12 text-sm text-gray-500">
              <span className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-primary-600" />
              جاري جلب البيانات...
            </div>
          )}

          {failed && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {hasResult && children}
        </div>

        {/* Footer */}
        {!scanning && !(captured && isLookingUp && !hasResult) && (
          <div className="flex gap-2 border-t border-gray-100 px-5 py-3">
            <button type="button" onClick={scanNext} className="btn-secondary flex-1">
              <ArrowPathIcon className="h-4 w-4" />
              {failed ? 'إعادة المسح' : 'مسح التالي'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">
              إغلاق
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanModal;
